/**
 * 🔒 The check stock drawn under a check (T3126; decision 7630, amending 0700).
 * The product owner, 2026-10-25: *"we only print the added text but put a
 * backdrop in to make it look like a check since they are pre printed"*. A
 * check payment's PDF tab and the Check template's preview draw the six values
 * the application prints over a picture of the pre-printed laser voucher stock:
 * the organization's block, the bank's block, "Pay to the order of", the dollar
 * box, the date and memo lines, the signature line, the MICR band and the stubs'
 * rules.
 *
 * 🔒 The figures are the common check-on-top voucher stock's, measured inside the
 * check band from the sheet's top left, and every one is remembered rather than
 * read off a published layout: the build machine reaches no network. Piece 27
 * carries them as an open issue. An organization that uploads a scan of its own
 * blank check is drawn over that picture instead.
 *
 * 🔨 Drawn by the server into the screen's HTML alone, and never on a sheet that
 * prints: the stock already carries every one of these.
 */
import { CHECK_BAND_HEIGHT_INCHES, PRINT_BAND_ORIGIN_INCHES } from './templates';

/** 🔒 Where the stock is perforated, in inches down the sheet: the band origins and nothing else (decision 0558). */
export const CHECK_STOCK_PERFORATIONS_INCHES: readonly number[] = [PRINT_BAND_ORIGIN_INCHES.StubBand, PRINT_BAND_ORIGIN_INCHES.SecondStubBand];

/**
 * 🔒 The MICR clear band: the bottom five-eighths of an inch of the check, where
 * the bank's magnetic line is printed and where nothing else may be (the check
 * standard's clear band). 🔨 Nothing this application draws belongs in it.
 */
export const CHECK_MICR_CLEAR_BAND_INCHES = 0.625;

/** One pre-printed box or line on the check band, in inches from the sheet's top left. */
export interface CheckStockMark {
  key: 'date' | 'amount' | 'payTo' | 'words' | 'memo' | 'signature';
  xInches: number;
  yInches: number;
  widthInches: number;
  /** A box's depth; zero on a line, which is drawn along the mark's top edge. */
  heightInches: number;
}

/**
 * 🔒 The stock's boxes and lines on the check band (decisions 0700, 7630). Each
 * holds one of the values the application prints: the date box Region 15, the
 * dollar box Region 16, the pay-to line Region 14, the words line Region 12 and
 * the memo line Region 13. The signature line stands on the right above the clear
 * band, where the stock prints it and nobody's printer should.
 *
 * 🔨 The memo line stands beside the signature, above it on the right, and never
 * on the left: the payee's address fills the left inside the envelope's window, and
 * the clear band starts at 2.875 inches (decision 7630).
 */
export const CHECK_STOCK_MARKS: readonly CheckStockMark[] = [
  { key: 'date', xInches: 6.75, yInches: 0.9, widthInches: 1.5, heightInches: 0.35 },
  { key: 'amount', xInches: 6.75, yInches: 1.38, widthInches: 1.5, heightInches: 0.35 },
  { key: 'payTo', xInches: 1.25, yInches: 1.72, widthInches: 5.35, heightInches: 0 },
  { key: 'words', xInches: 1.25, yInches: 2.07, widthInches: 6.5, heightInches: 0 },
  { key: 'memo', xInches: 4.85, yInches: 2.37, widthInches: 2.95, heightInches: 0 },
  { key: 'signature', xInches: 5, yInches: 2.7, widthInches: 3, heightInches: 0 },
];

/** 🔒 The organization's printed block at the check's top left, and the bank's beside it (decision 7630). */
export const CHECK_STOCK_ORGANIZATION_BLOCK = { xInches: 0.5, yInches: 0.35, widthInches: 3.2 } as const;
export const CHECK_STOCK_BANK_BLOCK = { xInches: 3.9, yInches: 0.35, widthInches: 2.5 } as const;

/**
 * 🔒 The rules a voucher stub carries, in inches down its own band (decision
 * 7630): one under the date and payee, one above the total.
 */
export const CHECK_STOCK_STUB_RULES_INCHES: readonly number[] = [0.68, 2.85];

/** 🔒 A stub rule's inset from each page edge, in inches (T3136; decision 7630). */
export const CHECK_STOCK_STUB_RULE_INSET_INCHES = 0.69;

/** 🔒 The MICR digits' place inside the clear band, in inches from its left and top (T3136; decision 7630). */
export const CHECK_STOCK_MICR_PADDING_INCHES = { left: 1, top: 0.18 } as const;

/** Where the clear band starts, in inches down the sheet: the check band's foot less the band. */
export function checkMicrClearBandTopInches(): number {
  return CHECK_BAND_HEIGHT_INCHES - CHECK_MICR_CLEAR_BAND_INCHES;
}

/**
 * 🔒 The organization's own blank check, uploaded once per bank account on the
 * Check template page (decision 7630): a PNG or a JPEG, at most two megabytes,
 * because the screen carries the picture inline in the sheet it draws.
 */
export const CHECK_STOCK_MIME_TYPES = ['image/png', 'image/jpeg'] as const;
export const CHECK_STOCK_MAX_BYTES = 2 * 1024 * 1024;

/** 🔒 The seeded type every check stock picture is filed under, the books' class and nobody's download (decision 7630). */
export const CHECK_STOCK_FILE_TYPE_NAME = 'Check Stock';

/** Which of the two rules a stock upload failed, or null where it failed neither; the type is judged first. */
export function checkStockProblem(upload: { mimeType: string; sizeBytes: number }): 'type' | 'size' | null {
  if (!(CHECK_STOCK_MIME_TYPES as readonly string[]).includes(upload.mimeType)) return 'type';
  if (upload.sizeBytes > CHECK_STOCK_MAX_BYTES) return 'size';
  return null;
}

/** One bank account's stock as the Check template page lists it: the account and the picture it holds, if any. */
export interface CheckStockAccountRow {
  bankAccountId: string;
  name: string;
  institution: string | null;
  stock: { fileId: string; filename: string; mimeType: string; sizeBytes: number } | null;
}

/** What the stock route answers: every bank account a check can be drawn on, by name. */
export interface CheckStockListAnswer {
  accounts: CheckStockAccountRow[];
}

/** 🔒 The stock routes, beside the templates they are drawn under (decision 7630). */
export const CHECK_STOCK_PATH = '/settings/print-templates/check-stock';
export function checkStockAccountPath(bankAccountId: string): string {
  return `${CHECK_STOCK_PATH}/${encodeURIComponent(bankAccountId)}`;
}
